import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { getRepLevel, getRepType } from "../../../../../utils/helpers";
import { SkillLevel } from "../../../../workers/types";
import SkillRepresentationCell from "./column-skill-representations-cell";

interface ApproveSkillUpgradeDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (selectedLevelId: number, comment: string) => void;
  workerFirstName: string;
  skillName: string;
  requestedLevel: SkillLevel;
  defaultSelectedLevelId: number;
  allSkillLevels: SkillLevel[];
}

const ApproveSkillUpgradeDialog: React.FC<ApproveSkillUpgradeDialogProps> = ({
  open,
  onClose,
  onConfirm,
  workerFirstName,
  skillName,
  requestedLevel,
  defaultSelectedLevelId,
  allSkillLevels,
}) => {
  const { t } = useTranslation();
  const [selectedLevelId, setSelectedLevelId] = useState<number>(
    defaultSelectedLevelId
  );
  const [comment, setComment] = useState("");

  useEffect(() => {
    if (open) {
      setSelectedLevelId(defaultSelectedLevelId);
      setComment("");
    }
  }, [open, defaultSelectedLevelId]);

  const handleConfirm = () => {
    onConfirm(selectedLevelId, comment.trim());
  };

  const handleClose = () => {
    onClose();
  };

  const isCommentTooLong = comment.length > 250;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="approve-skill-upgrade-dialog-title"
    >
      <DialogTitle id="approve-skill-upgrade-dialog-title">
        {t("cross-skilling_upgrade-skill-for-worker")}
      </DialogTitle>
      <DialogContent sx={{ minWidth: 600 }}>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {t("cross-skilling_approve-upgrade-prefix")}{" "}
          <strong>{workerFirstName}</strong>
          {t("cross-skilling_approve-upgrade-mid")}{" "}
          <Tooltip title={skillName} disableInteractive>
            <strong
              style={{
                display: "inline-block",
                maxWidth: "200px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                verticalAlign: "bottom",
              }}
            >
              {skillName}
            </strong>
          </Tooltip>
          {t("cross-skilling_approve-upgrade-suffix")}
        </Typography>
        <TextField
          select
          fullWidth
          label={t("cross-skilling_approve-at-level")}
          value={selectedLevelId}
          onChange={(e) => setSelectedLevelId(Number(e.target.value))}
          variant="filled"
          sx={{ mb: 2 }}
        >
          {allSkillLevels.map((level) => (
            <MenuItem key={level.id} value={level.id}>
              <Box display="flex" flexDirection="row" alignItems="center">
                <SkillRepresentationCell
                  level={level.level}
                  value={level.value}
                  backgroundColor={level.backColorCode}
                  foregroundColor={level.foreColorCode}
                  representationType={getRepType(level.repCode)}
                  representationLevel={getRepLevel(level.repCode, level.level)}
                  border={1}
                  backgroundWidth="35px"
                  backgroundHeight="35px"
                />
                <Typography ml={1}>{level.name}</Typography>
                {level.id === requestedLevel.id && (
                  <Typography
                    component="span"
                    variant="caption"
                    sx={{ ml: 1, color: "text.secondary" }}
                  >
                    {t("cross-skilling_requested")}
                  </Typography>
                )}
              </Box>
            </MenuItem>
          ))}
        </TextField>
        <TextField
          multiline
          rows={3}
          fullWidth
          label={t("cross-skilling_supervisor-comments-optional")}
          variant="filled"
          placeholder={t("cross-skilling_comment-visible-to-worker")}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          error={isCommentTooLong}
          helperText={
            isCommentTooLong
              ? t("cross-skilling_comment-max-length")
              : ""
          }
        />
        <Typography
          sx={{
            fontSize: "12px",
            color: isCommentTooLong ? "red" : "#00000099",
            display: "flex",
            justifyContent: "flex-end",
            fontWeight: 400,
          }}
        >
          {comment.length} / 250
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose}>{t("common_cancel")}</Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={isCommentTooLong}
        >
          {t("cross-skilling_upgrade")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ApproveSkillUpgradeDialog;
